/**
 * Pyramide en perspective cavalière, SOMMETS NOMMÉS, partagée par les
 * automatismes 3e 1239 (nature d'une face) et 1240 (patrons).
 *
 * Base régulière à n côtés (3, 4, 6) ou rectangle (n = 4, base 'rectangle'),
 * posée sur une ellipse de perspective (profondeur écrasée, léger décalage
 * vers la droite), sommet à l'aplomb du centre. Arêtes cachées en pointillés :
 * les arêtes touchant un sommet arrière gauche ou central (convention de
 * svgPyramide 4e : CD, DA et SD pour SABCD). Aucune hauteur ni codage : la figure ne dit que la forme.
 *
 * Le SVG porte data-solide="pyramide;base=carre;sommet=S;base=ABCD" pour les
 * solveurs (la structure nommée, pas la géométrie écran).
 */

const NOIR = '#111827';
const R1 = (v) => Math.round(v * 10) / 10;

export const NOM_BASE = { carre: 'carrée', rectangle: 'rectangulaire', triangle: 'triangulaire', hexagone: 'hexagonale' };
export const N_DE = { carre: 4, rectangle: 4, triangle: 3, hexagone: 6 };

/**
 * @param {{ base: 'carre'|'rectangle'|'triangle'|'hexagone', sommet: string, lettres: string[] }} cfg
 *   lettres : les sommets de la base dans l'ordre (avant-gauche, avant-droit, puis vers l'arrière).
 */
export const svgPyramideNommee = ({ base = 'carre', sommet = 'S', lettres = ['A', 'B', 'C', 'D'] }) => {
    const n = N_DE[base];
    const W = 250; const H = 215;
    const cx = 105; const cy = 165; const R = base === 'rectangle' ? 84 : 66; const k = 0.5; const shear = 0.75;
    const ry = base === 'rectangle' ? 0.62 : 1;
    // angles : côté avant horizontal (n pair), sommet arrière unique (n = 3)
    const angles = [];
    // n = 3 : le sommet arrière est décalé à gauche pour que ses arêtes cachées ne se confondent pas avec les vues
    for (let i = 0; i < n; i++) angles.push(n === 3 ? [210, 330, 115][i] : 270 - 180 / n + (i * 360) / n);
    const P = angles.map((deg) => {
        const t = (deg * Math.PI) / 180;
        const prof = Math.sin(t) * ry;                       // > 0 : arrière
        // caché : sommet arrière situé à gauche ou au centre (la fuyante part vers la droite)
        return { x: R1(cx + R * Math.cos(t) + shear * R * k * prof), y: R1(cy - R * k * prof), arriere: prof > 0.05 && Math.cos(t) <= 0.05 };
    });
    const S = { x: R1(cx + shear * R * k * 0.15), y: 26 };
    const ligne = (A, B, cachee) => `<line x1="${A.x}" y1="${A.y}" x2="${B.x}" y2="${B.y}" stroke="${NOIR}" stroke-width="${cachee ? 1.3 : 1.8}"${cachee ? ' stroke-dasharray="5 4"' : ''}/>`;
    let s = '';
    for (let i = 0; i < n; i++) {
        const A = P[i]; const B = P[(i + 1) % n];
        s += ligne(A, B, A.arriere || B.arriere);
    }
    for (let i = 0; i < n; i++) s += ligne(S, P[i], P[i].arriere);
    // étiquettes : repoussées depuis le centre de la base
    P.forEach((p, i) => {
        const dx = p.x - cx; const dy = p.y - cy;
        const d = Math.hypot(dx, dy) || 1;
        s += `<text x="${R1(p.x + (dx / d) * 14)}" y="${R1(p.y + (dy / d) * 14 + 5)}" font-size="14" font-weight="700" fill="${NOIR}" text-anchor="middle">${lettres[i]}</text>`;
    });
    s += `<text x="${S.x}" y="${S.y - 8}" font-size="14" font-weight="700" fill="${NOIR}" text-anchor="middle">${sommet}</text>`;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-solide="pyramide;base=${base};sommet=${sommet};base=${lettres.join('')}" role="img" aria-label="Une pyramide ${sommet}${lettres.join('')} à base ${NOM_BASE[base]} en perspective cavalière, arêtes cachées en pointillés.">${s}</svg>`;
};
